import {
  ClientModel,
  IsFavorited,
  Lookup,
  OneToMany,
  OneToOne,
  PolymorphicOneToMany,
  PolymorphicReference,
  Reference,
  UserFavorites,
} from './decorators';

import {
  assignedTaskStore,
  auditStore,
  commentStore,
  coRegistrantStore,
  customerAddressStore,
  dealStore,
  emojiReactionsStore,
  favoriteStore,
  fileMetadataStore,
  hubStore,
  lenderStore,
  notificationStore,
  paymentStore,
  priceBreakdownStore,
  primaryCustomerStore,
  protectionPlanStore,
  smsAgentMessageStore,
  taskStore,
  tradeStore,
  userStore,
  vehicleFromIndexerStore,
  vehicleListingStore,
  vehicleLocationStore,
} from './stores/index';

import {
  AppraisalOfferStatus,
  type AppraisalOfferStatusType,
} from '@/lib/appraisal-constants';
import { appraisalOfferStoreV2 } from '@/models/stores/appraisal-offers-v2.ts';
import { appraisalStoreV2 } from '@/models/stores/appraisals-v2.ts';
import { confirmationHistoryStore } from '@/models/stores/confirmation-history.ts';
import { confirmationsStore } from '@/models/stores/confirmations.ts';
import { instantOfferStoreV2 } from '@/models/stores/instant-offers-v2.ts';
import { replyStore } from '@/models/stores/replies.ts';
import { sellerStore } from '@/models/stores/sellers.ts';
import { taskStoreV2, taskTemplateStore } from '@/models/stores/tasks-v2.ts';
import dayjs from 'dayjs';
import { computed, makeObservable, observable, toJS } from 'mobx';
import { AIDealSuggestionModel, AITaskSuggestionModel } from './ai';
import type {
  AIDealSuggestion as AIDealSuggestionType,
  AITaskSuggestion as AITaskSuggestionType,
} from './ai/types';
import {
  AppraisalModelV2,
  AppraisalOfferModelV2,
  AssignedTaskModel,
  AuctionModel,
  AuditModel,
  CommentModel,
  ConfirmationHistoryModel,
  ConfirmationModel,
  CoreDealModel,
  CustomerAddressModel,
  CustomerLeadModel,
  CustomerModel,
  EmojiReactionModel,
  FavoriteModel,
  FileMetadataModel,
  HubModel,
  InstantOfferModelV2,
  LenderModel,
  NotificationModel,
  PaymentModel,
  PriceBreakdownModel,
  ProtectionPlanModel,
  ProtectionPlanNameModel,
  ProtectionPlanTermLengthModel,
  ProtectionPlanTermMilesModel,
  ProtectionPlanTypeModel,
  ReplyModel,
  SellerModel,
  SMSAgentMessageModel,
  TaskModel,
  TaskModelV2,
  TaskTemplateModel,
  TradeModel,
  UserModel,
  VehicleFromIndexerModel,
  VehicleImageModel,
  VehicleListingModel,
  VehicleLocationModel,
} from './index';
import { vehicleImageStore } from './stores/vehicle-images';
import type {
  AppraisalOfferV2 as AppraisalOfferV2Type,
  AppraisalV2 as AppraisalV2Type,
  AssignedTask as AssignedTaskType,
  Auction as AuctionType,
  AuditLog,
  Audit as AuditType,
  Comment as CommentType,
  ConfirmationHistoryEntry as ConfirmationHistoryType,
  Confirmation as ConfirmationType,
  CoreDeal as CoreDealType,
  Address as CustomerAddressType,
  CustomerLead as CustomerLeadType,
  Customer as CustomerType,
  EmojiReaction as EmojiReactionType,
  Favorite as FavoriteType,
  FileMetadata as FileMetadataType,
  Hub as HubType,
  InstantOfferV2 as InstantOfferV2Type,
  Lender as LenderType,
  Notification as NotificationType,
  Payment as PaymentType,
  PriceBreakdown as PriceBreakdownType,
  ProtectionPlanName as ProtectionPlanNameType,
  ProtectionPlanTermLength as ProtectionPlanTermLengthType,
  ProtectionPlanTermMiles as ProtectionPlanTermMilesType,
  ProtectionPlan as ProtectionPlanType,
  ProtectionPlanType as ProtectionPlanTypeType,
  Reply as ReplyType,
  Seller as SellerType,
  SMSAgentMessage as SMSAgentMessageType,
  TaskTemplate as TaskTemplateType,
  Task as TaskType,
  TaskV2 as TaskTypeV2,
  Trade as TradeType,
  UserFavorite,
  User as UserType,
  VehicleFromIndexer as VehicleFromIndexerType,
  VehicleImage as VehicleImageType,
  VehicleListing as VehicleListingType,
  VehicleLocation as VehicleLocationType,
} from './types';
import {
  CommentTargetType,
  ConfirmationTargetType,
  EmojiReactionType as ReactionType,
  TaskableType,
} from './types';

// Base interface for all entity models
export interface BaseModel {
  id: string; // id will be the uuid string on all models
  [key: string]: any; // allow dynamic property access for foreign keys
}

// Model registry definitions
export type EntityModel =
  | typeof Auction
  | typeof VehicleLocation
  | typeof Favorite
  | typeof Notification
  | typeof Hub
  | typeof User
  | typeof Deal
  | typeof Comment
  | typeof Confirmation
  | typeof ConfirmationHistory
  | typeof EmojiReaction
  | typeof Reply
  | typeof PrimaryCustomer
  | typeof CoRegistrant
  | typeof CustomerAddress
  | typeof ProtectionPlan
  | typeof ProtectionPlanName
  | typeof ProtectionPlanTypeEntity
  | typeof ProtectionPlanTermLength
  | typeof ProtectionPlanTermMiles
  | typeof Lender
  | typeof PriceBreakdown
  | typeof FileMetadata
  | typeof Payment
  | typeof Trade
  | typeof Audit
  | typeof TaskTemplate
  | typeof TaskV2
  | typeof Task
  | typeof AssignedTask
  | typeof AIDealSuggestion
  | typeof VehicleListing
  | typeof VehicleImage
  | typeof VehicleFromIndexer
  | typeof SMSAgentMessage
  | typeof Seller
  | typeof CustomerLead
  | typeof AppraisalV2
  | typeof AppraisalOfferV2
  | typeof InstantOfferV2;

export const modelRegistry = new Map<string, EntityModel>();

/**
 * User Model for users
 */
@ClientModel('User')
export class User extends UserModel implements BaseModel {
  /** one to many notifications */
  @OneToMany(() => notificationStore)
  get notifications(): Notification[] {
    return [];
  }

  /** many to many reference to favorites */
  @UserFavorites(() => favoriteStore)
  get favorites(): UserFavorite[] {
    return [];
  }

  constructor(attrs: UserType) {
    super(attrs);
  }
}

/**
 * Favorite Model for favorites
 */
@ClientModel('Favorite')
export class Favorite extends FavoriteModel implements BaseModel {
  constructor(attrs: FavoriteType) {
    super(attrs);
  }

  @Lookup(() => hubStore, 'hub_id')
  get hub(): Hub | null {
    return null;
  }

  @Lookup(() => userStore, 'user_id')
  get user(): User | null {
    return null;
  }

  @Lookup(() => dealStore, 'deal_id')
  get deal(): Deal | null {
    return null;
  }
}

/**
 *  the notifications model belongs to the user model
 */
@ClientModel('Notification')
export class Notification extends NotificationModel implements BaseModel {
  constructor(attrs: NotificationType) {
    super(attrs);

    makeObservable(this);
  }

  @Lookup(() => dealStore, 'deal_id')
  get deal(): Deal | null {
    return null;
  }

  @Lookup(() => userStore, 'triggered_by_user_id')
  get triggeredByUser(): User | null {
    return null;
  }

  @Lookup(() => userStore, 'user_id')
  get user(): User | null {
    return null;
  }

  get isRead(): boolean {
    return this.read_at !== null && this.read_at !== undefined;
  }
}

/**
 * Hub Model for hubs
 */
@ClientModel('Hub')
export class Hub extends HubModel implements BaseModel {
  /** one to many deals */
  @OneToMany(() => dealStore)
  get deals(): Deal[] {
    return [];
  }

  constructor(attrs: HubType) {
    super(attrs);
  }
}

/**
 * Deal
 */
@ClientModel('Deal')
export class Deal extends CoreDealModel implements BaseModel {
  /** many to one reference to hub */
  @Reference(() => hubStore)
  get hub(): Hub | null {
    return null;
  }

  @Lookup(() => userStore, 'finance_manager_id')
  @computed
  get finance_manager(): User | null {
    return null;
  }

  @Lookup(() => userStore, 'primary_sales_rep_id')
  @computed
  get primary_sales_rep(): User | null {
    return null;
  }

  @Lookup(() => userStore, 'secondary_sales_rep_id')
  @computed
  get secondary_sales_rep(): User | null {
    return null;
  }

  /** many to many reference to favorites */
  @IsFavorited(() => favoriteStore)
  get is_favorited(): boolean {
    return false;
  }

  /** one to one primary customer */
  @OneToOne(() => primaryCustomerStore)
  @computed
  get primary_customer(): PrimaryCustomer | null {
    return null;
  }

  /** one to one co-registrant */
  @OneToOne(() => coRegistrantStore)
  @computed
  get co_registrant(): CoRegistrant | null {
    return null;
  }

  @Lookup(() => vehicleListingStore, 'vehicle_listing_id')
  @computed
  get vehicle_listing(): VehicleListing | null {
    return null;
  }

  /** one to one protection plan */
  @OneToOne(() => protectionPlanStore)
  @computed
  get protection_plan(): ProtectionPlan | null {
    return null;
  }

  /** one to one lender */
  @Lookup(() => lenderStore, 'lender_id')
  @computed
  get lender(): Lender | null {
    return null;
  }

  /** one to one price breakdown */
  @OneToOne(() => priceBreakdownStore)
  @computed
  get price_breakdown(): PriceBreakdown | null {
    return null;
  }

  @Lookup(() => vehicleLocationStore, 'vehicle_current_location_id')
  get vehicle_current_location(): VehicleLocation | null {
    return null;
  }

  /** one-to-many polymorphic comments */
  /** one-to-many comments */
  @PolymorphicOneToMany(() => commentStore, CommentTargetType.Deal, (a, b) =>
    dayjs(a.created_at).diff(dayjs(b.created_at)),
  )
  @computed
  get comments(): Comment[] {
    return [];
  }

  /** one-to-many emoji reactions */
  @PolymorphicOneToMany(() => emojiReactionsStore, ReactionType.Deal)
  @computed
  get emoji_reactions(): EmojiReaction[] {
    return [];
  }

  /** one to many files */
  @OneToMany(() => fileMetadataStore)
  @computed
  get files(): FileMetadata[] {
    return [];
  }

  /** one to many payments */
  @OneToMany(() => paymentStore)
  @computed
  get payments(): Payment[] {
    return [];
  }

  /** one to many trade ins */
  @OneToMany(() => tradeStore)
  @computed
  get trades(): Trade[] {
    return [];
  }

  /** one to many trade-in appraisals (appraisals_v2.deal_id → deal.id) */
  @OneToMany(() => appraisalStoreV2)
  @computed
  get appraisals(): AppraisalV2[] {
    return [];
  }

  @computed
  get audit_log(): AuditLog[] {
    const retVal: AuditLog[] = [];

    const allTogether = [...this.audits, ...this.comments].sort((a, b) => {
      return dayjs(a.created_at).diff(dayjs(b.created_at));
    });

    allTogether.forEach((item) => {
      retVal.push(
        item instanceof Audit
          ? { id: item.id, deal_id: item.deal_id, type: 'audit' }
          : { id: item.id, deal_id: item.commentable_id, type: 'comment' },
      );
    });

    return retVal;
  }

  /** one to many audits */
  @OneToMany(() => auditStore)
  @computed
  get audits(): Audit[] {
    return [];
  }

  @OneToMany(() => assignedTaskStore)
  @computed
  get assigned_tasks(): AssignedTask[] {
    return [];
  }

  @PolymorphicOneToMany(() => taskStoreV2, TaskableType.Deal)
  get tasks(): TaskV2[] {
    return [];
  }

  @OneToMany(
    () => smsAgentMessageStore,
    (a: SMSAgentMessage, b: SMSAgentMessage) =>
      dayjs(a.message_date).diff(dayjs(b.message_date)),
  )
  @computed
  get sms_agent_messages(): SMSAgentMessage[] {
    return [];
  }

  /**
   * Serializes the deal to a plain JSON object with all computed properties
   * This is useful for LLM calls where you need the full object structure
   */
  toJSON(): Record<string, any> {
    return toJS({
      // Core properties
      id: this.id,
      vehicle_listing_id: this.vehicle_listing_id,
      created_at: this.created_at,
      last_updated: this.last_updated,
      short_id: this.short_id,
      status: this.status,
      stage: this.stage,
      cdk_id: this.cdk_id,
      hub_path_identifier: this.hub_path_identifier,
      delivery_preference: this.delivery_preference,
      delivery_date: this.delivery_date,
      pickup_date: this.pickup_date,
      date_added_to_customer_delivery_queue:
        this.date_added_to_customer_delivery_queue,
      favorited: this.favorited,
      priority: this.priority,
      funding_method: this.funding_method,
      risk: this.risk,
      ai_summary: this.ai_summary,
      sms_conversation: this.sms_conversation,
      agent_convo_paused: this.agent_convo_paused,
      vehicle_delivery_odometer: this.vehicle_delivery_odometer,
      rapid_recon_step: this.rapid_recon_step,
      sms_flow: this.sms_flow,
      sms_flow_step: this.sms_flow_step,
      finance_manager_id: this.finance_manager_id,
      primary_sales_rep_id: this.primary_sales_rep_id,
      secondary_sales_rep_id: this.secondary_sales_rep_id,
      lender_id: this.lender_id,

      // Computed getters
      hub: this.hub,
      finance_manager: this.finance_manager,
      primary_sales_rep: this.primary_sales_rep,
      secondary_sales_rep: this.secondary_sales_rep,
      is_favorited: this.is_favorited,
      primary_customer: this.primary_customer,
      co_registrant: this.co_registrant,
      vehicle_listing: this.vehicle_listing,
      protection_plan: this.protection_plan,
      lender: this.lender,
      price_breakdown: this.price_breakdown,
      comments: this.comments,
      emoji_reactions: this.emoji_reactions,
      files: this.files,
      payments: this.payments,
      trades: this.trades,
      audit_log: this.audit_log,
      audits: this.audits,
      assigned_tasks: this.assigned_tasks,
      tasks: this.tasks,
      sms_agent_messages: this.sms_agent_messages,
    });
  }

  constructor(attrs: CoreDealType) {
    super(attrs);

    makeObservable(this);
  }
}

/**
 * The primary customer model belongs to the deal model.
 * i.e. a deal can have one primary customer.
 */
@ClientModel('PrimaryCustomer')
export class PrimaryCustomer extends CustomerModel implements BaseModel {
  constructor(attrs: CustomerType) {
    super(attrs);

    makeObservable(this);
  }

  @OneToMany(() => customerAddressStore)
  get addresses(): CustomerAddress[] {
    return [];
  }

  @Reference(() => dealStore)
  get deal(): Deal | null {
    return null;
  }
}

/**
 * The co-registrant model belongs to the deal model.
 * i.e. a deal can have one co-registrant.
 */
@ClientModel('CoRegistrant')
export class CoRegistrant extends CustomerModel implements BaseModel {
  constructor(attrs: CustomerType) {
    super(attrs);

    makeObservable(this);
  }

  @OneToMany(() => customerAddressStore)
  get addresses(): CustomerAddress[] {
    return [];
  }

  @Reference(() => dealStore)
  get deal(): Deal | null {
    return null;
  }
}

@ClientModel('CustomerAddress')
export class CustomerAddress extends CustomerAddressModel implements BaseModel {
  constructor(attrs: CustomerAddressType) {
    super(attrs);
  }
}

/**
 * The protection plan model belongs to the deal model.
 * i.e. a deal can have one protection plan.
 */
@ClientModel('ProtectionPlan')
export class ProtectionPlan extends ProtectionPlanModel implements BaseModel {
  deal_id: string;

  constructor(attrs: ProtectionPlanType) {
    super(attrs);
    this.deal_id = attrs.deal_id;

    makeObservable(this, {
      id: observable,
      name: observable,
      key: observable,
      type: observable,
      term_length: observable,
      term_miles: observable,
      monthly_price: observable,
      price: observable,
      deal_id: observable,
      deal: computed,
    });
  }

  @Reference(() => dealStore)
  get deal(): Deal | null {
    return null;
  }
}

/**
 * Protection plan name model
 */
@ClientModel('ProtectionPlanName')
export class ProtectionPlanName
  extends ProtectionPlanNameModel
  implements BaseModel
{
  constructor(attrs: ProtectionPlanNameType) {
    super(attrs);
  }
}

/**
 * Protection plan type model
 */
@ClientModel('ProtectionPlanType')
export class ProtectionPlanTypeEntity
  extends ProtectionPlanTypeModel
  implements BaseModel
{
  constructor(attrs: ProtectionPlanTypeType) {
    super(attrs);
  }
}

/**
 * Protection plan term length model
 */
@ClientModel('ProtectionPlanTermLength')
export class ProtectionPlanTermLength
  extends ProtectionPlanTermLengthModel
  implements BaseModel
{
  constructor(attrs: ProtectionPlanTermLengthType) {
    super(attrs);
  }
}

/**
 * Protection plan term miles model
 */
@ClientModel('ProtectionPlanTermMiles')
export class ProtectionPlanTermMiles
  extends ProtectionPlanTermMilesModel
  implements BaseModel
{
  constructor(attrs: ProtectionPlanTermMilesType) {
    super(attrs);
  }
}

/**
 * The lender model can belong to a deal, or it can live on its own
 */
@ClientModel('Lender')
export class Lender extends LenderModel implements BaseModel {
  constructor(attrs: LenderType) {
    super(attrs);
  }

  @Reference(() => dealStore, { nullable: true })
  get deal(): Deal | null {
    // todo: implement this
    return null;
  }
}

/**
 * The price breakdown model belongs to the deal model.
 * i.e. a deal can have one price breakdown.
 */
@ClientModel('PriceBreakdown')
export class PriceBreakdown extends PriceBreakdownModel implements BaseModel {
  constructor(attrs: PriceBreakdownType) {
    super(attrs);
  }

  @Reference(() => dealStore)
  get deal(): Deal | null {
    return null;
  }
}

@ClientModel('Confirmation')
export class Confirmation extends ConfirmationModel implements BaseModel {
  confirmation_to_id: string;
  confirmation_to_type: ConfirmationTargetType;

  constructor(attrs: ConfirmationType) {
    super(attrs);

    this.confirmation_to_id = attrs.confirmation_to_id;
    this.confirmation_to_type = attrs.confirmation_to_type;

    makeObservable(
      this,
      {
        target: computed,
        confirmed: observable,
        most_recent_entry_id: observable,
        most_recent_entry: computed,
      },
      { autoBind: true },
    );
  }

  @OneToMany(
    () => confirmationHistoryStore,
    (a, b) => dayjs(b.created_at).diff(dayjs(a.created_at)),
  )
  get history(): ConfirmationHistory[] {
    return [];
  }

  @PolymorphicReference<Deal | Trade>(
    () => ({
      deal: dealStore,
      trade: tradeStore,
    }),
    'target_type',
    'target_id',
  )
  get target(): Deal | Trade | null {
    return null;
  }

  @Lookup(() => confirmationHistoryStore, 'most_recent_entry_id')
  get most_recent_entry(): ConfirmationHistory | null {
    return null;
  }
}

@ClientModel('ConfirmationHistory')
export class ConfirmationHistory
  extends ConfirmationHistoryModel
  implements BaseModel
{
  constructor(attrs: ConfirmationHistoryType) {
    super(attrs);

    makeObservable(this, {
      confirmation: computed,
      value: observable.ref,
      created_at: observable,
      confirmed_by: computed,
    });
  }

  @Reference(() => confirmationsStore)
  get confirmation(): Confirmation | null {
    return null;
  }

  @Lookup(() => userStore, 'user_id')
  get confirmed_by(): User | null {
    return null;
  }
}

/**
 * The EmojiReaction model: belongs to the deal model.
 * i.e. a deal can have many reactions.
 */
@ClientModel('EmojiReaction')
export class EmojiReaction extends EmojiReactionModel implements BaseModel {
  reaction_to_id: string;
  reaction_to_type: ReactionType;

  constructor(attrs: EmojiReactionType) {
    super(attrs);

    this.reaction_to_id = attrs.reaction_to_id;
    this.reaction_to_type = attrs.reaction_to_type;

    makeObservable(
      this,
      {
        target: computed,
        created_by: computed,
      },
      { autoBind: true },
    );
  }

  /** many-to-one reference to Deal/Comment/Reply */
  @PolymorphicReference<Deal | Comment | Reply>(
    () => ({
      deal: dealStore,
      comment: commentStore,
      reply: replyStore,
    }),
    'reaction_to_type',
    'reaction_to_id',
  )
  get target(): Deal | Comment | Reply | null {
    return null;
  }

  @Lookup(() => userStore, 'user_id')
  get created_by(): User | null {
    return null;
  }
}

@ClientModel('Comment')
export class Comment extends CommentModel implements BaseModel {
  commentable_id: string;
  commentable_type: CommentTargetType;

  /** many-to-one reference to Deal/Trade */
  @PolymorphicReference<Deal | Trade>(
    () => ({
      deal: dealStore,
      trade: tradeStore,
    }),
    'commentable_type',
    'commentable_id',
  )
  get commentable(): Deal | Trade | null {
    return null;
  }

  @Lookup(() => userStore, 'user_id')
  get created_by(): User | null {
    return null;
  }

  @OneToMany(
    () => replyStore,
    (a: Reply, b: Reply) => dayjs(a.created_at).diff(dayjs(b.created_at)),
  )
  get replies(): Reply[] {
    return [];
  }

  @PolymorphicOneToMany(() => emojiReactionsStore, ReactionType.Comment)
  get emoji_reactions(): EmojiReaction[] {
    return [];
  }

  constructor(attrs: CommentType) {
    super(attrs);

    // TODO (randy) -- move this into model after DealComment stuff is gone
    this.commentable_id = attrs.commentable_id;
    this.commentable_type = attrs.commentable_type;

    makeObservable(this, {
      commentable: computed,
      created_by: computed,
      replies: computed,
      emoji_reactions: computed,
      content: observable.ref,
      edited: observable,
      updated_at: observable,
    });
  }
}

@ClientModel('Reply')
export class Reply extends ReplyModel implements BaseModel {
  /** many-to-one reference to Comment */
  @Reference(() => commentStore)
  get comment(): Comment | null {
    return null;
  }

  @Lookup(() => userStore, 'user_id')
  get created_by(): User | null {
    return null;
  }

  /** one-to-many emoji reactions */
  @PolymorphicOneToMany(() => emojiReactionsStore, ReactionType.Reply)
  get emoji_reactions(): EmojiReaction[] {
    return [];
  }

  constructor(attrs: ReplyType) {
    super(attrs);

    makeObservable(this, {
      comment: computed,
      created_by: computed,
      emoji_reactions: computed,
      content: observable.ref,
      edited: observable,
      updated_at: observable,
    });
  }
}

/**
 * The file metadata model belongs to the deal model.
 * i.e. a deal can have many files
 */
@ClientModel('FileMetadata')
export class FileMetadata extends FileMetadataModel implements BaseModel {
  constructor(attrs: FileMetadataType) {
    super(attrs);
  }

  @Reference(() => dealStore)
  get deal(): Deal | null {
    return null;
  }
}

/**
 * The payment model belongs to the deal model.
 * i.e. a deal can have many payments.
 */
@ClientModel('Payment')
export class Payment extends PaymentModel implements BaseModel {
  constructor(attrs: PaymentType) {
    super(attrs);

    makeObservable(this);
  }

  @Reference(() => dealStore)
  get deal(): Deal | null {
    return null;
  }
}

/**
 * the trade in model belongs to the deal model.
 * i.e. a deal can have many trade ins.
 */
@ClientModel('Trade')
export class Trade extends TradeModel implements BaseModel {
  constructor(attrs: TradeType) {
    super(attrs);

    makeObservable(this, {
      created_by: computed,
      createdBy: computed,
      deal: computed,
      comments: computed,
      emoji_reactions: computed,
    });
  }

  @PolymorphicOneToMany(() => commentStore, CommentTargetType.Trade, (a, b) =>
    dayjs(a.created_at).diff(dayjs(b.created_at)),
  )
  get comments(): Comment[] {
    return [];
  }

  @PolymorphicOneToMany(() => emojiReactionsStore, ReactionType.Trade)
  get emoji_reactions(): EmojiReaction[] {
    return [];
  }

  @Lookup(() => userStore, 'created_by_id')
  get created_by(): User | null {
    return null;
  }

  @Reference(() => dealStore)
  get deal(): Deal | null {
    return null;
  }

  @Lookup(() => userStore, 'created_by_id')
  get createdBy(): User | null {
    return null;
  }
}

/**
 * The audit model belongs to the deal model.
 * i.e. a deal can have many audits.
 */
@ClientModel('Audit')
export class Audit extends AuditModel implements BaseModel {
  constructor(attrs: AuditType) {
    super(attrs);
  }

  @Reference(() => dealStore)
  get deal(): Deal | null {
    return null;
  }

  @Lookup(() => userStore, 'user_id')
  get created_by(): User | null {
    return null;
  }
}

@ClientModel('TaskTemplate')
export class TaskTemplate extends TaskTemplateModel implements BaseModel {
  constructor(attrs: TaskTemplateType) {
    super(attrs);
  }
}

@ClientModel('TaskV2')
export class TaskV2 extends TaskModelV2 implements BaseModel {
  constructor(attrs: TaskTypeV2) {
    super(attrs);
  }

  @Lookup(() => taskTemplateStore, 'task_template_id')
  @computed
  get taskTemplate(): TaskTemplate | null {
    return null;
  }

  // TODO -- remove after v1 task stuff is removed, duplicate getter w/ different name to make it easier to switch between both
  @Lookup(() => taskTemplateStore, 'task_template_id')
  @computed
  get task(): TaskTemplate | null {
    return null;
  }

  @PolymorphicReference<Deal>(
    () => ({
      deal: dealStore,
    }),
    'taskable_type',
    'taskable_id',
  )
  @computed
  get entity(): Deal | null {
    return null;
  }

  @PolymorphicReference<User>(
    () => ({
      user: userStore,
    }),
    'assignee_type',
    'assignee_id',
  )
  @computed
  get assignee_temp(): User | null {
    return null;
  }
}

/**
 * The task model belongs to the deal model.
 * i.e. a deal can have many tasks.
 */
@ClientModel('Task')
export class Task extends TaskModel implements BaseModel {
  constructor(attrs: TaskType) {
    super(attrs);
  }
}

@ClientModel('AssignedTask')
export class AssignedTask extends AssignedTaskModel implements BaseModel {
  constructor(attrs: AssignedTaskType) {
    super(attrs);
  }

  @Lookup(() => taskStore, 'task_id')
  get task(): Task | null {
    return null;
  }

  @Lookup(() => dealStore, 'deal_id')
  get deal(): Deal | null {
    return null;
  }

  @Lookup(() => userStore, 'assignee_id')
  get assigneeUser(): User | null {
    return null;
  }
}

/**
 * The auction model for trades
 */
@ClientModel('Auction')
export class Auction extends AuctionModel implements BaseModel {
  constructor(attrs: AuctionType) {
    super(attrs);
  }
}

@ClientModel('VehicleLocation')
export class VehicleLocation extends VehicleLocationModel implements BaseModel {
  constructor(attrs: VehicleLocationType) {
    super(attrs);
  }
}

@ClientModel('AIDealSuggestion')
export class AIDealSuggestion
  extends AIDealSuggestionModel
  implements BaseModel
{
  constructor(attrs: AIDealSuggestionType) {
    super(attrs);
  }
}

@ClientModel('AITaskSuggestion')
export class AITaskSuggestion
  extends AITaskSuggestionModel
  implements BaseModel
{
  constructor(attrs: AITaskSuggestionType) {
    super(attrs);
  }
}

@ClientModel('VehicleListing')
export class VehicleListing extends VehicleListingModel implements BaseModel {
  constructor(attrs: VehicleListingType) {
    super(attrs);
  }

  // todo: this is a hack to get the deal from the vehicle listing
  get deal(): Deal | null {
    const id = vehicleListingStore.storeReferenceToDealByVehicleListingId.get(
      this.id,
    );
    if (!id) return null;
    const deal = dealStore.getById(id);
    if (!deal) return null;
    return deal;
  }

  @Lookup(() => vehicleFromIndexerStore, 'vehicle_id')
  get vehicle(): VehicleFromIndexer | null {
    return null;
  }

  @OneToMany(() => vehicleImageStore)
  get images(): VehicleImage[] {
    return [];
  }

  /**
   * YMMT: Year, Make, Model, Trim
   */
  get YMMT(): string {
    return `${this.vehicle?.year} ${this.vehicle?.make} ${this.vehicle?.model} ${this.vehicle?.trim}`;
  }

  /**
   * link to vehicle listing
   */
  get link() {
    return `https://www.evercars.com/cars/${this.vehicle?.year}-${this.vehicle?.make}-${this.vehicle?.model}-${this.vehicle?.trim}-${this.vehicle?.vin}`;
  }
}

@ClientModel('VehicleImage')
export class VehicleImage extends VehicleImageModel implements BaseModel {
  constructor(attrs: VehicleImageType) {
    super(attrs);
  }
}

@ClientModel('VehicleFromIndexer')
export class VehicleFromIndexer
  extends VehicleFromIndexerModel
  implements BaseModel
{
  constructor(attrs: VehicleFromIndexerType) {
    super(attrs);
  }
}

@ClientModel('SMSAgentMessage')
export class SMSAgentMessage extends SMSAgentMessageModel implements BaseModel {
  constructor(attrs: SMSAgentMessageType) {
    super(attrs);
  }

  @Lookup(() => dealStore, 'deal_id')
  get deal(): Deal | null {
    return null;
  }
}

@ClientModel('Seller')
export class Seller extends SellerModel implements BaseModel {
  constructor(attrs: SellerType) {
    super(attrs);

    makeObservable(
      this,
      {
        first_name: observable,
        last_name: observable,
        middle_name: observable,
        phone_number: observable,
        email: observable,
      },
      { autoBind: true },
    );
  }
}

@ClientModel('CustomerLead')
export class CustomerLead extends CustomerLeadModel implements BaseModel {
  constructor(attrs: CustomerLeadType) {
    super(attrs);
  }
}

// V2 Registry Classes
@ClientModel('AppraisalV2')
export class AppraisalV2 extends AppraisalModelV2 implements BaseModel {
  constructor(attrs: AppraisalV2Type) {
    super(attrs);

    makeObservable(
      this,
      {
        seller: computed,
        offers: computed,
        offer_status: computed,
        instant_offers: computed,
        instant_offers_ready: computed,
        comments: computed,

        id: observable,
        tracking_id: observable,
        product_user_id: observable,
        deal_id: observable,
        product_marketplace_listing_id: observable,
        vin: observable,
        make: observable,
        model: observable,
        year: observable,
        zip_code: observable,
        phone_number: observable,
        seller_name: observable,
        email: observable,
        drivetrain: observable,
        trim: observable,
        body: observable,
        mileage: observable,
        exterior_color: observable,
        interior_color: observable,
        seating_capacity: observable,
        num_of_keys: observable,
        condition: observable,
        num_of_accidents: observable,
        accident_type: observable,
        exterior_damage_types: observable,
        exterior_damage_description: observable,
        interior_damage_types: observable,
        interior_damage_description: observable,
        mechanical_damage_types: observable,
        mechanical_damage_description: observable,
        self_driving_capability: observable,
        highlights: observable,
        status: observable,
        is_upgrade_or_purchase_interested: observable,
        upgrade_or_purchase_interested_info: observable,
        debt_type: observable,
        tires_condition: observable,
        exterior_panel_damage_count: observable,
        creation_step: observable,
        is_serviced_regularly: observable,
        is_contacted_by_sales: observable,
        sell_by: observable,
        is_just_browsing: observable,
        source: observable,
        metadata: observable,
        created_at: observable,
        updated_at: observable,
      },
      { autoBind: true },
    );
  }

  @OneToMany(
    () => appraisalOfferStoreV2,
    (a, b) => dayjs(a.created_at).diff(dayjs(b.created_at)),
  )
  get offers(): AppraisalOfferV2[] {
    return [];
  }

  @OneToMany(() => instantOfferStoreV2)
  get instant_offers(): InstantOfferV2[] {
    return [];
  }

  get instant_offers_ready(): InstantOfferV2[] {
    return this.instant_offers;
  }

  @Lookup(() => sellerStore, 'user_id')
  get seller(): Seller | null {
    return null;
  }

  /** one-to-many polymorphic comments */
  @PolymorphicOneToMany(
    () => commentStore,
    CommentTargetType.Appraisal,
    (a, b) => dayjs(a.created_at).diff(dayjs(b.created_at)),
  )
  get comments(): Comment[] {
    return [];
  }

  get offer_status(): AppraisalOfferStatusType {
    if (!this.offers || this.offers.length === 0)
      return AppraisalOfferStatus.NoAvailableOffers;
    for (const offer of this.offers) {
      if (offer.status === 'ACCEPTED')
        return AppraisalOfferStatus.OffersAccepted;
    }
    return AppraisalOfferStatus.OffersAvailable;
  }
}

@ClientModel('AppraisalOfferV2')
export class AppraisalOfferV2
  extends AppraisalOfferModelV2
  implements BaseModel
{
  constructor(attrs: AppraisalOfferV2Type) {
    super(attrs);

    makeObservable(
      this,
      {
        id: observable,
        tracking_id: observable,
        offer_amount_dollars: observable,
        offer_type: observable,
        appraisal_v2_id: observable,
        status: observable,
        expires_at: observable,
        created_at: observable,
        updated_at: observable,
      },
      { autoBind: true },
    );
  }
}

@ClientModel('InstantOfferV2')
export class InstantOfferV2 extends InstantOfferModelV2 implements BaseModel {
  constructor(attrs: InstantOfferV2Type) {
    super(attrs);

    makeObservable(
      this,
      {
        id: observable,
        tracking_id: observable,
        channel: observable,
        channel_id: observable,
        state: observable,
        status: observable,
        offer_type: observable,
        powertrain_type: observable,
        margin: observable,
        risk_adjustment: observable,
        risk_level: observable,
        max_bid_price: observable,
        start_bid_price: observable,
        sales_price_estimate: observable,
        sales_price_estimate_source: observable,
        is_upgrade_or_purchase_interested: observable,
      },
      { autoBind: true },
    );
  }
}
